import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { ordersAPI, warehouseAPI } from '../../services/api';
import api from '../../services/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Search, Eye, Package, Truck, ChevronDown, ChevronUp,
  DollarSign, CheckCircle, AlertCircle, Clock, XCircle, Ban,
  Loader2, FileText, CreditCard, MapPin, Calendar, User, Hash, Download, Mail, Send
} from 'lucide-react';
import PdfViewer from '../../components/PdfViewer';
import { Checkbox } from '../../components/ui/checkbox';
import { DialogFooter } from '../../components/ui/dialog';

/* ─── Status Config ───────────────────────────────────────────── */
const ORDER_STATUS = {
  pending:              { label: 'Pending',           color: 'bg-amber-50 text-amber-700 border-amber-200',   icon: Clock,        dot: 'bg-amber-400' },
  confirmed:            { label: 'Confirmed',         color: 'bg-blue-50 text-blue-700 border-blue-200',      icon: CheckCircle,  dot: 'bg-blue-500' },
  processing:           { label: 'Processing',        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',icon: Package,      dot: 'bg-indigo-500' },
  shipped:              { label: 'Shipped',           color: 'bg-purple-50 text-purple-700 border-purple-200',icon: Truck,        dot: 'bg-purple-500' },
  partially_delivered:  { label: 'Partial Delivery',  color: 'bg-orange-50 text-orange-700 border-orange-200',icon: Truck,        dot: 'bg-orange-400' },
  delivered:            { label: 'Delivered',         color: 'bg-green-50 text-green-700 border-green-200',   icon: CheckCircle,  dot: 'bg-green-500' },
  cancelled:            { label: 'Cancelled',         color: 'bg-red-50 text-red-600 border-red-200',         icon: XCircle,      dot: 'bg-red-400' },
};

const PAYMENT_STATUS = {
  paid:   { label: 'Paid',   color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  unpaid: { label: 'Unpaid', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
};

const INVOICE_STATUS = {
  unpaid:    { label: 'Unpaid',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid:      { label: 'Paid',      color: 'bg-green-50 text-green-700 border-green-200' },
  overdue:   { label: 'Overdue',   color: 'bg-red-50 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const StatusBadge = ({ config, className = '' }) => {
  if (!config) return null;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${config.color} ${className}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {config.label}
    </span>
  );
};

const InfoCell = ({ icon: Icon, label, children, className = '' }) => (
  <div className={className}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </p>
    <div className="text-sm">{children}</div>
  </div>
);

/* ─── Main Component ──────────────────────────────────────────── */
const Orders = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDelivery, setShowDelivery] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deliveryWarehouse, setDeliveryWarehouse] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOrderId, setPdfOrderId] = useState(null);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [carrierTracking, setCarrierTracking] = useState('');
  const [downloadDialogOrderId, setDownloadDialogOrderId] = useState(null);

  // Multi-select and follow-up email
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // single order or null for batch
  const [personalMessage, setPersonalMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const getInvoiceDirectUrl = (orderId) => {
    const token = localStorage.getItem('at1s_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/invoice?token=${token}`;
  };

  const load = useCallback(async () => {
    try {
      const [ordersData, whData] = await Promise.all([ordersAPI.getAll(), warehouseAPI.getAll()]);
      setOrders(ordersData);
      setWarehouses(whData);
    } catch {
      toast({ title: 'Error', description: 'Failed to load orders', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const fmtCur = (v) => `$${(v || 0).toFixed(2)}`;

  /* ─── Delivery ─── */
  const openDeliveryDialog = async (order) => {
    setShowDelivery(order);
    setDeliveryWarehouse(warehouses.length > 0 ? warehouses[0].warehouse_id : '');
    setDeliveryNotes('');
    setCarrierTracking('');
    try {
      const dlvs = await ordersAPI.getDeliveries(order.id);
      setDeliveries(dlvs);
      const deliveredMap = {};
      dlvs.forEach(d => d.items?.forEach(item => {
        const key = `${item.size}|${item.merv}`;
        deliveredMap[key] = (deliveredMap[key] || 0) + item.quantity;
      }));
      setDeliveryItems(order.items.map(oi => {
        const key = `${oi.size}|${oi.merv}`;
        const delivered = deliveredMap[key] || 0;
        const remaining = oi.quantity - delivered;
        return { size: oi.size, merv: oi.merv, ordered: oi.quantity, delivered, remaining, deliver_qty: remaining > 0 ? String(remaining) : '0' };
      }).filter(i => i.remaining > 0));
    } catch {
      setDeliveries([]);
      setDeliveryItems(order.items.map(oi => ({ size: oi.size, merv: oi.merv, ordered: oi.quantity, delivered: 0, remaining: oi.quantity, deliver_qty: String(oi.quantity) })));
    }
  };

  const handleCreateDelivery = async () => {
    if (!deliveryWarehouse) { toast({ title: 'Required', description: 'Select a warehouse', variant: 'destructive' }); return; }
    const items = deliveryItems.filter(i => parseInt(i.deliver_qty) > 0).map(i => ({ size: i.size, merv: i.merv, quantity: Math.min(parseInt(i.deliver_qty), i.remaining) }));
    if (items.length === 0) { toast({ title: 'Required', description: 'Enter qty for at least one item', variant: 'destructive' }); return; }
    setFormLoading(true);
    try {
      await ordersAPI.createDelivery(showDelivery.id, { warehouse_id: deliveryWarehouse, items, carrier_tracking_number: carrierTracking || null, notes: deliveryNotes });
      toast({ title: 'Processing', description: 'Delivery document created. Order moved to processing.' });
      setShowDelivery(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Delivery failed', variant: 'destructive' });
    } finally { setFormLoading(false); }
  };

  const handleInvoiceStatus = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateInvoiceStatus(orderId, newStatus);
      toast({ title: 'Updated', description: `Invoice marked as ${newStatus}` });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const openCancelDialog = (order) => { setShowCancel(order); setCancelReason(''); };

  const handleDownloadInvoice = (orderId) => {
    setDownloadDialogOrderId(orderId);
  };

  const handleViewInvoice = async (orderId) => {
    try {
      const dataUrl = await ordersAPI.viewInvoice(orderId);
      setPdfUrl(dataUrl);
      setPdfOrderId(orderId);
    } catch {
      toast({ title: 'Error', description: 'Failed to open invoice', variant: 'destructive' });
    }
  };

  const closePdfViewer = () => {
    setPdfUrl(null);
    setPdfOrderId(null);
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) { toast({ title: 'Required', description: 'Please provide a reason', variant: 'destructive' }); return; }
    setCancelLoading(true);
    try {
      const result = await ordersAPI.cancelOrder(showCancel.id, cancelReason);
      const msgs = ['Order cancelled.'];
      if (result.refund_processed) msgs.push('Refund processed.');
      if (result.email_sent) msgs.push('Customer notified.');
      toast({ title: 'Cancelled', description: msgs.join(' ') });
      setShowCancel(null);
      load();
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setCancelLoading(false); }
  };

  const filtered = orders.filter(o => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || (o.id || '').toLowerCase().includes(s) || (o.customer_name || '').toLowerCase().includes(s) || (o.invoice_number || '').toLowerCase().includes(s) || (o.company || '').toLowerCase().includes(s);
    return matchSearch && (statusFilter.size === 0 || statusFilter.has(o.status));
  });

  // Selection helpers
  const toggleOrderSelect = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOrders = () => {
    if (selectedOrderIds.size === filtered.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(filtered.map(o => o.id)));
    }
  };

  const openEmailForOrder = (order) => {
    setEmailTarget(order);
    setPersonalMessage('');
    setShowEmailDialog(true);
  };

  const openEmailForSelected = () => {
    setEmailTarget(null);
    setPersonalMessage('');
    setShowEmailDialog(true);
  };

  const handleSendFollowUp = async () => {
    const orderIds = emailTarget ? [emailTarget.id] : [...selectedOrderIds];
    if (orderIds.length === 0) return;
    setEmailSending(true);
    try {
      const res = await api.post('/email-logs/send-followup', {
        order_ids: orderIds,
        personal_message: personalMessage,
      });
      toast({ title: 'Emails Sent', description: res.data.message });
      setShowEmailDialog(false);
      setSelectedOrderIds(new Set());
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send', variant: 'destructive' });
    } finally { setEmailSending(false); }
  };

  const stats = {
    total: orders.length,
    revenue: orders.filter(o => o.payment_status === 'paid').reduce((s, o) => s + (o.total || 0), 0),
    pendingDelivery: orders.filter(o => ['pending', 'confirmed', 'processing', 'partially_delivered'].includes(o.status)).length,
    unpaidInvoices: orders.filter(o => o.invoice_status === 'unpaid' && o.status !== 'cancelled').length,
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-10 w-10 animate-spin text-sky-600" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-5" data-testid="orders-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track all customer orders</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Orders', value: stats.total, icon: Package, color: 'text-slate-800' },
            { label: 'Paid Revenue', value: fmtCur(stats.revenue), icon: DollarSign, color: 'text-green-600' },
            { label: 'Awaiting Shipment', value: stats.pendingDelivery, icon: Truck, color: 'text-amber-600' },
            { label: 'Unpaid Invoices', value: stats.unpaidInvoices, icon: FileText, color: 'text-red-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border p-4 flex items-center gap-3" data-testid={`stat-${i}`}>
              <div className={`w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center ${s.color}`}><s.icon className="h-5 w-5" /></div>
              <div>
                <p className="text-xs text-slate-500">{s.label}</p>
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by order ID, customer, invoice..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
          </div>
          <div className="relative">
            <button
              onClick={() => {
                const el = document.getElementById('status-filter-menu');
                if (el) el.classList.toggle('hidden');
              }}
              className="inline-flex items-center gap-2 h-9 px-3 text-sm border rounded-md bg-white hover:bg-slate-50 min-w-[180px] justify-between"
              data-testid="status-filter"
            >
              <span className="text-slate-700">
                {statusFilter.size === 0 ? 'All Statuses' : `${statusFilter.size} selected`}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <div id="status-filter-menu" className="hidden absolute z-50 mt-1 w-52 bg-white border rounded-md shadow-lg py-1">
              <button
                onClick={() => { setStatusFilter(new Set()); document.getElementById('status-filter-menu')?.classList.add('hidden'); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 text-sky-600 font-semibold"
              >
                Clear All
              </button>
              {Object.entries(ORDER_STATUS).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={statusFilter.has(k)}
                    onChange={() => {
                      setStatusFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(k)) next.delete(k); else next.add(k);
                        return next;
                      });
                    }}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-sky-600"
                  />
                  <span className={`w-2 h-2 rounded-full ${v.dot}`} />
                  <span className="text-slate-700">{v.label}</span>
                </label>
              ))}
            </div>
          </div>
          {selectedOrderIds.size > 0 && (
            <Button size="sm" onClick={openEmailForSelected} data-testid="send-batch-email-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white">
              <Mail className="h-4 w-4 mr-1.5" /> Email Selected ({selectedOrderIds.size})
            </Button>
          )}
          <p className="text-sm text-slate-400 ml-auto">{filtered.length} of {orders.length} orders</p>
        </div>

        {/* Select All Toggle */}
        {filtered.length > 0 && (
          <div className="flex items-center gap-2 px-1">
            <Checkbox
              checked={selectedOrderIds.size === filtered.length && filtered.length > 0}
              onCheckedChange={toggleSelectAllOrders}
              data-testid="select-all-orders-checkbox"
            />
            <span className="text-xs text-slate-500 select-none cursor-pointer" onClick={toggleSelectAllOrders}>
              {selectedOrderIds.size === filtered.length ? 'Deselect All' : 'Select All'}
            </span>
            {selectedOrderIds.size > 0 && (
              <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full ml-2">
                {selectedOrderIds.size} selected
              </span>
            )}
          </div>
        )}

        {/* Orders List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border p-16 text-center" data-testid="empty-state">
            <Package className="h-12 w-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500 font-medium">No orders found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(order => {
              const isOpen = expandedId === order.id;
              const os = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
              const ps = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.unpaid;
              const invs = INVOICE_STATUS[order.invoice_status] || INVOICE_STATUS.unpaid;
              const canDeliver = !['delivered', 'cancelled'].includes(order.status);
              const isCancelled = order.status === 'cancelled';

              return (
                <div key={order.id}
                  className={`bg-white rounded-xl border shadow-sm transition-shadow hover:shadow-md ${isCancelled ? 'opacity-60' : ''}`}
                  data-testid={`order-row-${order.id}`}>

                  {/* Order Row */}
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : order.id)}>
                    {/* Top line: Checkbox + ID + date + amount + expand */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedOrderIds.has(order.id)}
                            onCheckedChange={() => toggleOrderSelect(order.id)}
                            data-testid={`select-order-${order.id}`}
                          />
                        </div>
                        <div className={`w-2 h-2 rounded-full ${os.dot} mt-1.5 flex-shrink-0`} />
                        <div>
                          <p className="font-mono text-sm font-bold text-slate-800" data-testid={`order-id-${order.id}`}>{order.id}</p>
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {fmt(order.order_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-lg font-bold text-slate-800">{fmtCur(order.total)}</p>
                        {isOpen ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                      </div>
                    </div>

                    {/* Info grid: Customer | Order Status | Payment | Invoice | Delivery */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-3">
                      <InfoCell icon={User} label="Customer">
                        <p className="font-medium text-slate-700 truncate">{order.customer_name}</p>
                        {order.company && <p className="text-xs text-slate-400 truncate">{order.company}</p>}
                      </InfoCell>

                      <InfoCell icon={Package} label="Order Status">
                        <StatusBadge config={os} />
                      </InfoCell>

                      <InfoCell icon={CreditCard} label="Payment">
                        <StatusBadge config={ps} />
                      </InfoCell>

                      <InfoCell icon={FileText} label="Invoice">
                        <div className="flex items-center gap-2">
                          <StatusBadge config={invs} />
                          {order.invoice_number && (
                            <span className="font-mono text-[10px] text-slate-400" data-testid={`invoice-num-${order.id}`}>
                              {order.invoice_number}
                            </span>
                          )}
                        </div>
                      </InfoCell>

                      <InfoCell icon={Truck} label="Delivery">
                        {order.status === 'delivered' ? (
                          <span className="text-xs font-semibold text-green-600 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" /> Delivered</span>
                        ) : order.tracking_number ? (
                          <span className="font-mono text-xs text-sky-600">{order.tracking_number}</span>
                        ) : canDeliver ? (
                          <span className="text-xs text-slate-400">Not shipped</span>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </InfoCell>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isOpen && (
                    <div className="border-t bg-slate-50/50 p-4 space-y-4" data-testid={`order-details-${order.id}`}>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Items */}
                        <div className="lg:col-span-2">
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Order Items</p>
                          <div className="bg-white rounded-lg border overflow-hidden">
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 text-[10px] font-bold text-slate-500 uppercase">
                              <div className="col-span-5">Product</div>
                              <div className="col-span-2 text-center">Qty</div>
                              <div className="col-span-2 text-right">Unit Price</div>
                              <div className="col-span-3 text-right">Total</div>
                            </div>
                            {order.items?.map((item, idx) => (
                              <div key={`${item.size}-${item.merv}-${idx}`} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t text-sm items-center">
                                <div className="col-span-5 text-slate-700"><span className="text-teal-600 font-mono text-xs mr-1">[{item.product_id || `AT1SM${item.merv}-${item.size}`}]</span>{item.size} <span className="text-slate-400">MERV {item.merv}</span></div>
                                <div className="col-span-2 text-center text-slate-600">{item.quantity}</div>
                                <div className="col-span-2 text-right text-slate-500">{fmtCur(item.price)}</div>
                                <div className="col-span-3 text-right font-semibold text-slate-800">{fmtCur(item.quantity * item.price)}</div>
                              </div>
                            ))}
                            <div className="border-t bg-slate-50 px-3 py-2 space-y-0.5 text-sm text-right">
                              {order.discount > 0 && <p><span className="text-slate-400 mr-4">Discount</span><span className="text-green-600 font-medium">-{fmtCur(order.discount)}</span></p>}
                              <p><span className="text-slate-400 mr-4">Shipping</span><span className="text-slate-600">{order.shipping === 0 ? 'FREE' : fmtCur(order.shipping)}</span></p>
                              <p><span className="text-slate-400 mr-4">Tax</span><span className="text-slate-600">{fmtCur(order.tax)}</span></p>
                              <p className="pt-1 border-t text-base font-bold"><span className="text-slate-500 mr-4">Total</span><span className="text-slate-800">{fmtCur(order.total)}</span></p>
                            </div>
                          </div>
                        </div>

                        {/* Details Panel */}
                        <div className="space-y-3">
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Order Details</p>

                          <div className="bg-white rounded-lg border p-3 space-y-3">
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 uppercase">Shipping Address</p>
                              <p className="text-sm text-slate-700 mt-0.5">{order.shipping_address || 'Not provided'}</p>
                            </div>
                            {(order.qbo_charge_id || order.stripe_payment_intent_id) && (
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase">Payment Reference</p>
                                {order.qbo_charge_id && <p className="font-mono text-xs text-green-600 mt-0.5 break-all" data-testid={`qbo-ref-${order.id}`}>QBO: {order.qbo_charge_id}</p>}
                                {order.stripe_payment_intent_id && <p className="font-mono text-xs text-sky-600 mt-0.5 break-all" data-testid={`stripe-ref-${order.id}`}>Stripe (legacy): {order.stripe_payment_intent_id}</p>}
                              </div>
                            )}
                            {order.is_cancelled && order.cancellation_reason && (
                              <div>
                                <p className="text-[10px] font-bold text-red-400 uppercase">Cancellation Reason</p>
                                <p className="text-sm text-red-600 mt-0.5">{order.cancellation_reason}</p>
                              </div>
                            )}
                          </div>

                          {/* Invoice Status Control */}
                          <div className="bg-white rounded-lg border p-3">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Update Invoice Status</p>
                            <select
                              className="w-full h-9 border rounded-md px-3 text-sm bg-white cursor-pointer focus:ring-2 focus:ring-sky-200 focus:outline-none"
                              value={order.invoice_status || 'unpaid'}
                              onChange={(e) => handleInvoiceStatus(order.id, e.target.value)}
                              data-testid={`invoice-status-${order.id}`}
                            >
                              <option value="unpaid">Unpaid</option>
                              <option value="paid">Paid</option>
                              <option value="overdue">Overdue</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEmailForOrder(order)}
                              className="w-full justify-start text-teal-700 border-teal-200 hover:bg-teal-50"
                              data-testid={`email-btn-${order.id}`}>
                              <Mail className="h-4 w-4 mr-2 text-teal-600" /> Send Follow-Up Email
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setSelectedOrder(order)} className="w-full justify-start"
                              data-testid={`view-btn-${order.id}`}>
                              <Eye className="h-4 w-4 mr-2 text-sky-600" /> View Full Details
                            </Button>
                            {order.payment_status === 'paid' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleViewInvoice(order.id)} className="w-full justify-start"
                                  data-testid={`view-invoice-btn-${order.id}`}>
                                  <Eye className="h-4 w-4 mr-2 text-blue-600" /> View Invoice
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleDownloadInvoice(order.id)} className="w-full justify-start"
                                  data-testid={`download-invoice-btn-${order.id}`}>
                                  <Download className="h-4 w-4 mr-2 text-emerald-600" /> Download Invoice
                                </Button>
                              </>
                            )}
                            {canDeliver && (
                              <Button size="sm" variant="outline" onClick={() => openDeliveryDialog(order)} className="w-full justify-start"
                                data-testid={`delivery-btn-${order.id}`}>
                                <Truck className="h-4 w-4 mr-2 text-teal-600" /> Create Delivery
                              </Button>
                            )}
                            {!isCancelled && (
                              <Button size="sm" variant="outline" onClick={() => openCancelDialog(order)}
                                className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`cancel-btn-${order.id}`}>
                                <Ban className="h-4 w-4 mr-2" /> Cancel Order
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Order Detail Dialog ─── */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Hash className="h-5 w-5 text-sky-600" />Order {selectedOrder.id}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Customer & Total */}
                <div className="flex items-start justify-between bg-slate-50 rounded-lg p-4">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase">Customer</p>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedOrder.customer_name}</p>
                    {selectedOrder.company && <p className="text-sm text-slate-500">{selectedOrder.company}</p>}
                    <p className="text-xs text-slate-400 mt-1">{fmt(selectedOrder.order_date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-400 uppercase">Order Total</p>
                    <p className="text-2xl font-bold text-sky-700 mt-0.5">{fmtCur(selectedOrder.total)}</p>
                  </div>
                </div>

                {/* Status Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1.5">Order Status</p>
                    <StatusBadge config={ORDER_STATUS[selectedOrder.status]} />
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1.5">Payment</p>
                    <StatusBadge config={PAYMENT_STATUS[selectedOrder.payment_status]} />
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1.5">Invoice</p>
                    <StatusBadge config={INVOICE_STATUS[selectedOrder.invoice_status]} />
                    {selectedOrder.invoice_number && <p className="font-mono text-[10px] text-slate-400 mt-1">{selectedOrder.invoice_number}</p>}
                  </div>
                </div>

                {/* Payment Ref */}
                {(selectedOrder.qbo_charge_id || selectedOrder.stripe_payment_intent_id) && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3" data-testid="payment-ref-section">
                    <p className="text-[10px] font-bold text-sky-600 uppercase mb-1">Payment Reference</p>
                    {selectedOrder.qbo_charge_id && <p className="font-mono text-sm text-green-700" data-testid="qbo-charge-detail">QBO Charge: {selectedOrder.qbo_charge_id}</p>}
                    {selectedOrder.stripe_payment_intent_id && <p className="font-mono text-sm text-slate-700" data-testid="stripe-pi-detail">Stripe (legacy): {selectedOrder.stripe_payment_intent_id}</p>}
                  </div>
                )}

                {/* Items Table */}
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">Items</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs font-bold text-slate-500">Product</th>
                          <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Qty</th>
                          <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Price</th>
                          <th className="text-right py-2 px-3 text-xs font-bold text-slate-500">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items?.map((item, idx) => (
                          <tr key={`${item.product_id || item.size}-${item.merv}-${idx}`} className="border-t"><td className="py-2 px-3"><span className="text-teal-600 font-mono text-xs mr-1">[{item.product_id || `AT1SM${item.merv}-${item.size}`}]</span> {item.size} MERV {item.merv}</td><td className="text-right py-2 px-3">{item.quantity}</td><td className="text-right py-2 px-3">{fmtCur(item.price)}</td><td className="text-right py-2 px-3 font-bold">{fmtCur(item.quantity * item.price)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="text-sm text-right space-y-0.5">
                  {selectedOrder.discount > 0 && <p>Discount: <span className="text-green-600">-{fmtCur(selectedOrder.discount)}</span></p>}
                  <p>Shipping: {selectedOrder.shipping === 0 ? 'FREE' : fmtCur(selectedOrder.shipping)}</p>
                  <p>Tax: {fmtCur(selectedOrder.tax)}</p>
                  <p className="text-lg font-bold text-slate-800 pt-1 border-t">Total: {fmtCur(selectedOrder.total)}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase mb-1">Shipping Address</p>
                  <p className="text-sm text-slate-700">{selectedOrder.shipping_address || '-'}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Delivery Dialog ─── */}
      <Dialog open={!!showDelivery} onOpenChange={() => setShowDelivery(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Delivery — {showDelivery?.id}</DialogTitle>
            <DialogDescription>Ship items from a warehouse. Inventory deducted automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {deliveries.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-bold text-slate-500 uppercase mb-1.5">Previous Deliveries</p>
                {deliveries.map((d, idx) => (
                  <div key={idx} className="text-xs text-slate-600 py-1.5 border-b border-slate-200 last:border-0" data-testid={`prev-delivery-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="h-3 w-3 text-green-500" />
                        <span className="font-mono font-bold">{d.id}</span>
                        <span>from {d.warehouse_name || d.warehouse_id}</span>
                      </div>
                      <Badge className={d.status === 'delivered' ? 'bg-green-100 text-green-700' : d.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}>{d.status?.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1 pl-5 text-slate-400">
                      <span>TRK: <span className="font-mono text-slate-600">{d.tracking_number}</span></span>
                      {d.carrier_tracking_number && <span>Carrier: <span className="font-mono text-slate-600">{d.carrier_tracking_number}</span></span>}
                    </div>
                    {d.status !== 'delivered' && (
                      <div className="flex gap-1 mt-1 pl-5">
                        {d.status === 'dispatched' && (
                          <button onClick={async () => { await ordersAPI.updateDeliveryStatus(showDelivery.id, d.id, 'in_transit'); toast({ title: 'Updated' }); openDeliveryDialog(showDelivery); }}
                            className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100" data-testid={`status-transit-${idx}`}>In Transit</button>
                        )}
                        <button onClick={async () => { await ordersAPI.updateDeliveryStatus(showDelivery.id, d.id, 'delivered'); toast({ title: 'Updated' }); openDeliveryDialog(showDelivery); load(); }}
                          className="text-[10px] px-2 py-0.5 bg-green-50 text-green-600 rounded hover:bg-green-100" data-testid={`status-delivered-${idx}`}>Delivered</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Ship From Warehouse *</Label>
              <select className="w-full h-9 border rounded-md px-2 text-sm bg-white" value={deliveryWarehouse} onChange={(e) => setDeliveryWarehouse(e.target.value)} data-testid="delivery-warehouse">
                <option value="">Select warehouse...</option>
                {warehouses.map(wh => <option key={wh.warehouse_id} value={wh.warehouse_id}>{wh.warehouse_id} — {wh.name}</option>)}
              </select>
            </div>
            {deliveryItems.length === 0 ? (
              <div className="text-center py-6 text-gray-500"><CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" /><p className="text-sm">All items fully delivered.</p></div>
            ) : (
              <div>
                <Label className="text-sm font-bold">Items to Ship</Label>
                <div className="space-y-2 mt-2">
                  {deliveryItems.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-lg border bg-slate-50" data-testid={`delivery-item-${idx}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-slate-700"><span className="text-teal-600 font-mono text-xs mr-1">[{item.product_id || `AT1SM${item.merv}-${item.size}`}]</span> {item.size} MERV {item.merv}</span>
                        <div className="text-xs text-slate-500">Ordered: {item.ordered} {item.delivered > 0 && <span className="text-green-600 ml-1">Shipped: {item.delivered}</span>} <span className="font-bold text-amber-600 ml-1">Left: {item.remaining}</span></div>
                      </div>
                      <div>
                        <Label className="text-xs">Qty to Ship (max {item.remaining})</Label>
                        <Input type="number" min="0" max={item.remaining} value={item.deliver_qty}
                          onChange={(e) => { const u = [...deliveryItems]; u[idx] = { ...u[idx], deliver_qty: e.target.value }; setDeliveryItems(u); }}
                          className="h-9" data-testid={`delivery-qty-${idx}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Carrier Tracking # (optional)</Label>
              <Input value={carrierTracking} placeholder="e.g., 1Z999AA10123456784" onChange={(e) => setCarrierTracking(e.target.value)} className="h-9" data-testid="carrier-tracking" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={deliveryNotes} placeholder="Optional notes..." onChange={(e) => setDeliveryNotes(e.target.value)} className="h-9" data-testid="delivery-notes" />
            </div>
            {deliveryItems.length > 0 && (
              <Button onClick={handleCreateDelivery} disabled={formLoading} className="w-full bg-gradient-to-r from-teal-500 to-sky-500" data-testid="submit-delivery-btn">
                {formLoading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing...</> : 'Create Delivery'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Cancel Dialog ─── */}
      <Dialog open={!!showCancel} onOpenChange={() => { if (!cancelLoading) setShowCancel(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="h-5 w-5" /> Cancel Order</DialogTitle>
          </DialogHeader>
          {showCancel && (
            <div className="space-y-4" data-testid="cancel-order-dialog">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-400">Order:</span> <span className="font-mono font-bold text-slate-800">{showCancel.id}</span></p>
                <p><span className="text-slate-400">Customer:</span> <span className="font-medium text-slate-800">{showCancel.customer_name}</span></p>
                <p><span className="text-slate-400">Total:</span> <span className="font-bold text-sky-700">{fmtCur(showCancel.total)}</span></p>
              </div>
              {showCancel.payment_status === 'paid' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="refund-warning">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Refund Required</p>
                    <p className="text-xs text-amber-700 mt-1">A full refund of <strong>{fmtCur(showCancel.total)}</strong> will need to be processed manually via QuickBooks.</p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason <span className="text-red-500">*</span></Label>
                <textarea className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Enter cancellation reason..." value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-input" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowCancel(null)} disabled={cancelLoading} data-testid="cancel-dialog-close-btn">Keep Order</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleCancelOrder} disabled={cancelLoading || !cancelReason.trim()} data-testid="confirm-cancel-btn">
                  {cancelLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><XCircle className="h-4 w-4 mr-2" /> {showCancel.payment_status === 'paid' ? 'Refund & Cancel' : 'Cancel Order'}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Invoice Viewer Modal */}
      <Dialog open={!!pdfUrl} onOpenChange={closePdfViewer}>
        <DialogContent className="max-w-4xl p-0 pb-2">
          <DialogHeader className="px-6 pt-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Invoice - {pdfOrderId}
            </DialogTitle>
            <DialogDescription>Invoice preview</DialogDescription>
          </DialogHeader>
          {pdfUrl && (
            <PdfViewer
              dataUrl={pdfUrl}
              orderId={pdfOrderId}
              onDownload={() => { closePdfViewer(); handleDownloadInvoice(pdfOrderId); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Download Dialog (Edge-compatible direct link) */}
      <Dialog open={!!downloadDialogOrderId} onOpenChange={() => setDownloadDialogOrderId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Invoice</DialogTitle>
            <DialogDescription>Use the link below to download or open the invoice PDF.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-5 text-center">
              <a
                href={downloadDialogOrderId ? getInvoiceDirectUrl(downloadDialogOrderId) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-bold text-teal-700 underline hover:text-teal-900 inline-flex items-center gap-2"
                data-testid="admin-invoice-direct-download-link"
              >
                <Download className="h-5 w-5" />
                Invoice-{downloadDialogOrderId}.pdf
              </a>
              <p className="text-xs text-slate-500 mt-3">
                Click the link above to open. If blocked, <strong>right-click</strong> and select <strong>"Save link as..."</strong>
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setDownloadDialogOrderId(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-Up Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-teal-600" /> Send Follow-Up Email
            </DialogTitle>
            <DialogDescription>
              {emailTarget
                ? <>Send to <strong>{emailTarget.customer_name}</strong> ({emailTarget.customer_email || 'No email'}) for order <strong>{emailTarget.id}</strong></>
                : <>Send individual follow-up emails to <strong>{selectedOrderIds.size} order(s)</strong>. Each customer will receive their own email with their order details.</>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Order summary for single */}
            {emailTarget && (
              <div className="bg-slate-50 border rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Order:</span><span className="font-mono font-bold">{emailTarget.id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><span>{emailTarget.status?.replace('_', ' ')}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment:</span><span className={emailTarget.payment_status === 'paid' ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>{emailTarget.payment_status}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total:</span><span className="font-bold">{fmtCur(emailTarget.total)}</span></div>
              </div>
            )}

            {/* Batch summary */}
            {!emailTarget && selectedOrderIds.size > 0 && (
              <div className="bg-slate-50 border rounded-lg p-3 text-sm max-h-40 overflow-y-auto">
                {[...selectedOrderIds].map(oid => {
                  const o = orders.find(x => x.id === oid);
                  return o ? (
                    <div key={oid} className="flex justify-between py-1 border-b last:border-0">
                      <span className="font-mono text-xs">{oid}</span>
                      <span className="text-xs text-slate-500">{o.customer_name} — {fmtCur(o.total)}</span>
                    </div>
                  ) : null;
                })}
              </div>
            )}

            {/* Pre-populated message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">Auto-included in email:</p>
              <p className="text-xs text-amber-700">Order details, items, totals, and a prompt to complete the order will be automatically included.</p>
            </div>

            {/* Personal message */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Personal Message (optional)</Label>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                className="w-full mt-1.5 border rounded-lg px-3 py-2 text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-teal-300"
                placeholder="Add a personal note to the customer (e.g., 'We noticed your order is incomplete. Please let us know if you need assistance.')..."
                data-testid="personal-message-input"
              />
            </div>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Mail className="h-3 w-3" /> A copy will be sent to contactat1s@air-techsolutions.com
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendFollowUp} disabled={emailSending}
              className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="confirm-send-followup-btn">
              <Send className="h-4 w-4 mr-1.5" />
              {emailSending ? 'Sending...' : emailTarget ? 'Send Email' : `Send ${selectedOrderIds.size} Email(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Orders;
